import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Users, Loader2 } from "lucide-react";

const EMPTY = { nom: "", prenom: "", email: "", poste: "", entreprise_id: "", type_contrat: "CDI", date_entree: "", salaire_brut: 0 };

export default function Salaries() {
  const [items, setItems] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    const [a, b] = await Promise.all([api.get("/salaries"), api.get("/entreprises")]);
    setItems(a.data); setEntreprises(b.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, salaire_brut: Number(form.salaire_brut) || 0 };
      if (!payload.entreprise_id) delete payload.entreprise_id;
      if (!payload.email) delete payload.email;
      await api.post("/salaries", payload);
      toast.success("Salarié ajouté"); setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const entLabel = (id) => entreprises.find(e => e.id === id)?.raison_sociale || "—";

  return (
    <div data-testid="salaries-page">
      <PageHeader
        title="Salariés"
        subtitle="Gestion des salariés du GE et missionnaires mis à disposition."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="salarie-add-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                <Plus className="h-4 w-4 mr-2" /> Nouveau salarié
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Ajouter un salarié</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Prénom *</Label><Input required value={form.prenom} onChange={(e)=>setForm({...form, prenom: e.target.value})} data-testid="sal-prenom" /></div>
                <div className="space-y-2"><Label>Nom *</Label><Input required value={form.nom} onChange={(e)=>setForm({...form, nom: e.target.value})} data-testid="sal-nom" /></div>
                <div className="space-y-2 col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} /></div>
                <div className="space-y-2"><Label>Poste</Label><Input value={form.poste} onChange={(e)=>setForm({...form, poste: e.target.value})} /></div>
                <div className="space-y-2">
                  <Label>Type contrat</Label>
                  <Select value={form.type_contrat} onValueChange={(v)=>setForm({...form, type_contrat: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["CDI","CDD","Intermittent","Apprentissage"].map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Entreprise utilisatrice</Label>
                  <Select value={form.entreprise_id} onValueChange={(v)=>setForm({...form, entreprise_id: v})}>
                    <SelectTrigger data-testid="sal-entreprise"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>{entreprises.map(e=><SelectItem key={e.id} value={e.id}>{e.raison_sociale}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Date entrée</Label><Input type="date" value={form.date_entree} onChange={(e)=>setForm({...form, date_entree: e.target.value})} /></div>
                <div className="space-y-2"><Label>Salaire brut (€)</Label><Input type="number" value={form.salaire_brut} onChange={(e)=>setForm({...form, salaire_brut: e.target.value})} /></div>
                <DialogFooter className="col-span-2"><Button type="submit" data-testid="sal-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Enregistrer</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <Users className="h-10 w-10 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">Aucun salarié enregistré.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="salaries-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Nom</th><th className="px-4 py-3">Poste</th><th className="px-4 py-3">Entreprise</th><th className="px-4 py-3">Contrat</th><th className="px-4 py-3 text-right">Brut</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{s.prenom} {s.nom}</td>
                  <td className="px-4 py-3 text-slate-600">{s.poste || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{entLabel(s.entreprise_id)}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700">{s.type_contrat}</span></td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">{(s.salaire_brut || 0).toLocaleString("fr-FR")} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
